import { useState, useRef, useEffect, useCallback } from 'react';
import { api, useAuthStore } from '@/store';
import { useNavigate } from 'react-router-dom';
import { Mic, X, Volume2, VolumeX } from 'lucide-react';
import { NexuLiveSession } from '@/lib/nexuLive';
import NexuThreeCore from '@/components/NexuThreeCore';

const endsWithQuestion = (t) => /\?\s*$/.test((t || '').trim());
const isFarewell = (t) => {
  const txt = (t || '').toLowerCase();
  // NÃO encerra se ainda há pergunta/ação em curso (o NEXU está oferecendo continuar)
  if (/\?|quer que|deseja|posso ajudar|precisa de|mais alguma|algo mais|quer (ver|saber|mudar|ajustar|modificar)/i.test(txt)) return false;
  // NÃO confundir contexto de conteúdo/ação com despedida
  if (/(série|serie|temporada|episódio|episodio|filme|elenco|estreia|lançad|mensagem|cupom|cliente|jogo)/i.test(txt)) return false;
  // encerra com frases de despedida do NEXU
  return /(tchau|até (mais|logo|breve|a próxima|amanhã)|pode fechar (o|a)|encerrando a sess(ã|a)o|sess(ã|a)o encerrada|fechando aqui|às ordens|as ordens|precisando (é |e )?(só |so )?(é |e )?(me )?chamar|precisar (é |e )?(só |so )?chamar|estarei (à|a) disposição|estou (à|a) disposição|fico (à|a) disposição|qualquer coisa (é |e )?(só |so )?chamar|boa noite|bom descanso|até a próxima|foi um prazer|disponível quando precisar)/i.test(txt);
};

// Despedida DITA PELO USUÁRIO — fecha na hora (ex.: "obrigado, pode fechar", "é só isso", "finaliza")
const userWantsToClose = (t) => {
  const txt = (t || '').toLowerCase().trim();
  if (txt.length < 3) return false;
  return /(^|\b)(pode (fechar|encerrar|finalizar)|fecha (isso|ai|aí|nexu)|finaliza(r)?|encerra(r)?|é só isso|era só isso|é isso|so isso|obrigad[oa],? (pode |)?(fechar|encerrar|finalizar|é só isso|era isso)|valeu,? pode fechar|tchau nexu|pode sair|fecha a tela|desativa|dispensad[oa])(\b|$)/i.test(txt);
};

// slots organizados na tela grande (desktop). Distribui os painéis ativos em posições que não se sobrepõem.
function computeSlots(activeKeys, isMobile) {
  if (isMobile) return {}; // no mobile, cada painel usa bottom sheet (não reposiciona)
  // posições disponíveis, em ordem de preenchimento (cantos e laterais da tela)
  const positions = [
    { left: 'auto', right: '1.5rem', top: '5rem', bottom: 'auto', width: '25rem' },   // direita-cima
    { left: '1.5rem', right: 'auto', top: '5rem', bottom: 'auto', width: '23rem' },   // esquerda-cima
    { left: 'auto', right: '1.5rem', top: 'auto', bottom: '5rem', width: '25rem', maxHeight: '42vh' }, // direita-baixo
    { left: '1.5rem', right: 'auto', top: 'auto', bottom: '5rem', width: '23rem', maxHeight: '42vh' }, // esquerda-baixo
    { left: '50%', right: 'auto', top: '5rem', bottom: 'auto', width: '24rem', transform: 'translateX(-50%)' }, // topo-centro
  ];
  const map = {};
  activeKeys.forEach((k, i) => { map[k] = positions[i] || positions[positions.length - 1]; });
  return map;
}

export default function NexuVoiceOverlay() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [filterPanel, setFilterPanel] = useState(null);
  const [actionPanel, setActionPanel] = useState(null); // { title, items: [{nome, integracao, status, detalhe}] }
  const [mediaPanel, setMediaPanel] = useState(null); // { tipo, titulo, itens/links }
  const [gamesPanel, setGamesPanel] = useState(null); // { jogos, total }
  const [gamesTab, setGamesTab] = useState('todos'); // todos | ao_vivo | encerrado | agendado
  const [messagePanel, setMessagePanel] = useState(null); // { mensagem, titulo }
  const [listPanel, setListPanel] = useState(null); // { tipo, titulo, itens } — cupons/templates
  const [inputPanel, setInputPanel] = useState(null); // { titulo, placeholder, campo }
  const [inputValue, setInputValue] = useState('');
  const [financePanel, setFinancePanel] = useState(null); // relatório financeiro
  const [closing, setClosing] = useState(false);
  const [epicMode, setEpicMode] = useState(() => { try { return localStorage.getItem('nexu_epic') === '1'; } catch { return false; } });
  const [flowSteps, setFlowSteps] = useState([]);
  const flowTurnRef = useRef(0); // marca o turno atual (agrupa ações do mesmo pedido no mesmo nível)
  const lastLevelIdsRef = useRef([]); // ids dos cards do último nível (para conectar como pais)
  const newTurnRef = useRef(true); // marca que o próximo flow_step inicia um NOVO passo (setado quando o usuário fala)
  // anexa dados ricos (filmes/clientes/jogos/links) ao último card do fluxo
  const attachRich = (kind, data) => {
    // mapeia o tipo de painel para as ferramentas compatíveis
    const toolMatch = {
      media: ['buscar_na_web'], games: ['jogos_do_dia'],
      filter: ['filtrar_clientes', 'listar_vencidos', 'listar_vencendo', 'listar_revendas', 'buscar_na_integracao'],
    };
    setFlowSteps(prev => {
      if (prev.length === 0) return prev;
      const copy = [...prev];
      // acha o último card compatível com esse tipo de dado rico (de trás pra frente)
      let idx = -1;
      for (let i = copy.length - 1; i >= 0; i--) {
        if ((toolMatch[kind] || []).includes(copy[i].tool)) { idx = i; break; }
      }
      if (idx === -1) idx = copy.length - 1; // fallback: último
      copy[idx] = { ...copy[idx], rich: { kind, data } };
      return copy;
    });
  };
  const [volume, setVolumeState] = useState(() => {
    try { const v = parseFloat(localStorage.getItem('nexu_volume')); return isNaN(v) ? 1 : v; } catch { return 1; }
  });
  const setVolume = setVolumeState;
  const changeVolume = (v) => {
    const nv = Math.max(0, Math.min(2, v));
    setVolume(nv);
    try { localStorage.setItem('nexu_volume', String(nv)); } catch {}
    if (liveRef.current && liveRef.current.setVolume) liveRef.current.setVolume(nv);
  };
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState('idle');      // idle | listening | thinking | speaking
  const [interim, setInterim] = useState('');
  const [reply, setReply] = useState('');
  const [typed, setTyped] = useState('');
  const [thinkMsg, setThinkMsg] = useState('');
  const [execMsg, setExecMsg] = useState('');
  const [prepMsg, setPrepMsg] = useState('');
  const [wakeEnabled, setWakeEnabled] = useState(() => {
    try { return localStorage.getItem('nexu_wake') === '1'; } catch { return false; }
  });

  const wakeRef = useRef(null);
  const recogRef = useRef(null);
  const audioRef = useRef(null);
  const liveRef = useRef(null);
  const liveReadyRef = useRef(false);
  const replyBufRef = useRef('');   // acumula a resposta do turno atual
  const inBufRef = useRef('');      // acumula a transcrição da fala do usuário
  const historyRef = useRef([]);
  const activeListenRef = useRef(false);
  const interruptRef = useRef(null);
  const interruptedRef = useRef(false);
  const fixedVoiceRef = useRef(null);
  const wakeEnabledRef = useRef(wakeEnabled);
  const startWakeRef = useRef(null);
  const micStreamRef = useRef(null);
  const activeRef = useRef(false);
  useEffect(() => { wakeEnabledRef.current = wakeEnabled; }, [wakeEnabled]);
  useEffect(() => { activeRef.current = active; }, [active]);

  // KEEPALIVE: a cada 2 min, se o NEXU NÃO estiver em uso, abre um WS rápido pra manter
  // o backend/proxy aquecido (evita o "timed out during opening handshake" após inatividade).
  useEffect(() => {
    const iv = setInterval(() => {
      if (activeRef.current) return; // em uso → não mexe
      try {
        const token = localStorage.getItem('nexus-token');
        if (!token) return;
        const base = process.env.REACT_APP_BACKEND_URL || '';
        const wsUrl = base.replace(/^http/, 'ws') + `/api/nexu/live?token=${encodeURIComponent(token)}&ping=1`;
        const ws = new WebSocket(wsUrl);
        let closed = false;
        const fecha = () => { if (!closed) { closed = true; try { ws.close(); } catch {} } };
        ws.onopen = () => { setTimeout(fecha, 500); };       // conectou → já pode fechar
        ws.onmessage = () => fecha;                           // recebeu algo → fecha
        ws.onerror = () => fecha();
        setTimeout(fecha, 6000);                              // trava de segurança
      } catch {}
    }, 120000); // 2 minutos
    return () => clearInterval(iv);
  }, []);

  const SR = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const nexuSlots = computeSlots([
    (gamesPanel && gamesPanel.jogos && gamesPanel.jogos.length > 0) ? 'games' : null,
    mediaPanel ? 'media' : null,
    (filterPanel && filterPanel.total > 0) ? 'filter' : null,
    listPanel ? 'list' : null,
    inputPanel ? 'input' : null,
    messagePanel ? 'message' : null,
    actionPanel ? 'action' : null,
    financePanel ? 'finance' : null,
  ].filter(Boolean), isMobile);
  const slotStyle = (key) => {
    if (isMobile) return undefined;
    const sl = nexuSlots[key];
    if (!sl) return { opacity: 0, pointerEvents: 'none' };
    return {
      position: 'absolute', left: sl.left, right: sl.right, top: sl.top, bottom: sl.bottom,
      width: sl.width, maxHeight: sl.maxHeight || '78vh', transform: sl.transform || 'none',
      transition: 'left .5s cubic-bezier(.2,.8,.2,1), right .5s cubic-bezier(.2,.8,.2,1), top .5s cubic-bezier(.2,.8,.2,1), bottom .5s cubic-bezier(.2,.8,.2,1), transform .5s cubic-bezier(.2,.8,.2,1), opacity .3s',
    };
  };
  const canUse = user && user.role !== 'vendedor';

  // ---------- áudio PCM -> WAV ----------
  const pcmToWav = (b64, sampleRate) => {
    const bin = atob(b64); const len = bin.length; const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
    const buffer = new ArrayBuffer(44 + len); const view = new DataView(buffer);
    const w = (o, s) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
    w(0, 'RIFF'); view.setUint32(4, 36 + len, true); w(8, 'WAVE'); w(12, 'fmt ');
    view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true); view.setUint16(34, 16, true); w(36, 'data'); view.setUint32(40, len, true);
    new Uint8Array(buffer, 44).set(bytes);
    return new Blob([buffer], { type: 'audio/wav' });
  };

  const getVoice = () => {
    if (fixedVoiceRef.current) return fixedVoiceRef.current;
    const vs = window.speechSynthesis.getVoices().filter(v => v.lang && v.lang.toLowerCase().startsWith('pt'));
    fixedVoiceRef.current = vs.find(v => /natural/i.test(v.name)) || vs.find(v => /google/i.test(v.name))
      || vs.find(v => v.lang.toLowerCase() === 'pt-br') || vs[0] || null;
    return fixedVoiceRef.current;
  };

  // fala priorizando SEMPRE a voz neural do Gemini; navegador só se a neural falhar de vez
  const stopAudio = () => {
    try { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } } catch {}
    try { window.speechSynthesis?.cancel(); } catch {}
  };

  const startInterruptListener = () => {
    if (!SR) return;
    try { interruptRef.current?.abort(); } catch {}
    const ir = new SR();
    ir.lang = 'pt-BR'; ir.continuous = true; ir.interimResults = true;
    ir.onresult = (e) => {
      const t = Array.from(e.results).map(r => r[0].transcript).join('').trim();
      if (t.length >= 2) {  // usuário começou a falar → interrompe
        interruptedRef.current = true;
        try { ir.stop(); } catch {}
        stopAudio();
        setTimeout(() => listen(), 100);
      }
    };
    ir.onerror = () => {};
    interruptRef.current = ir;
    try { ir.start(); } catch {}
  };
  const stopInterruptListener = () => { try { interruptRef.current?.abort(); } catch {} interruptRef.current = null; };

  // gera o áudio de UMA frase (retorna a url do wav ou null p/ fallback)
  const genAudio = async (frase) => {
    try {
      const r = await api.post('/nexu/tts', { text: frase });
      const rate = parseInt((String(r.data.mime).match(/rate=(\d+)/) || [])[1] || '24000');
      return URL.createObjectURL(pcmToWav(r.data.audio, rate));
    } catch { return null; }
  };
  const playUrl = (url) => new Promise((resolve) => {
    const a = new Audio(url); audioRef.current = a;
    a.onended = () => { URL.revokeObjectURL(url); resolve(); };
    a.onerror = () => resolve();
    a.play().catch(() => resolve());
  });
  const playBrowser = (frase) => new Promise((resolve) => {
    if (!window.speechSynthesis) return resolve();
    const u = new SpeechSynthesisUtterance(frase.replace(/[*#_`>]/g, ''));
    u.lang = 'pt-BR'; u.rate = 1.0;
    const v = getVoice(); if (v) u.voice = v;
    u.onend = () => resolve(); u.onerror = () => resolve();
    window.speechSynthesis.speak(u);
  });

  // fala a resposta em UMA requisição (evita estourar o rate limit por minuto do TTS)
  const speak = useCallback(async (text) => {
    if (!text) return;
    stopAudio();
    interruptedRef.current = false;
    setPhase('speaking');
    const url = await genAudio(text);
    if (interruptedRef.current) { stopInterruptListener(); return; }
    if (url) await playUrl(url);
    else await playBrowser(text);  // fallback navegador se o TTS falhar
    stopInterruptListener();
  }, []);

  // ---------- enviar ao NEXU ----------
  const sendToNexu = useCallback(async (text) => {
    if (!text) return;
    setPhase('thinking'); setReply('');
    try {
      const r = await api.post('/nexu/chat', { message: text, history: historyRef.current.slice(-10) });
      const rep = r.data.text || 'Pronto, Senhor.';
      historyRef.current.push({ role: 'user', text }, { role: 'model', text: rep });
      setReply(rep);
      await speak(rep);
      if (endsWithQuestion(rep) && !isFarewell(rep)) {
        setTimeout(() => listen(), 300);
      } else if (isFarewell(rep)) {
        setTimeout(() => closeOverlay(), 1400);
      } else {
        setPhase('idle');
        setTimeout(() => { if (activeRef.current && phaseRef.current === 'idle') closeOverlay(); }, 3500);
      }
    } catch {
      await speak('Tive um problema, Senhor.');
      setTimeout(() => closeOverlay(), 1200);
    }
  }, [speak]);

  const phaseRef = useRef('idle');
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // efeito de digitação do texto de resposta
  useEffect(() => {
    if (!reply) { setTyped(''); return; }
    setTyped('');
    let i = 0;
    const id = setInterval(() => {
      i++;
      setTyped(reply.slice(0, i));
      if (i >= reply.length) clearInterval(id);
    }, 22);  // velocidade da digitação
    return () => clearInterval(id);
  }, [reply]);

  // status dinâmicos enquanto processa (dá sensação de trabalho, não de travado)
  useEffect(() => {
    if (phase !== 'thinking') return;
    const msgs = ['Verificando seu pedido', 'Consultando o sistema', 'Analisando os dados',
                  'Organizando as informações', 'Quase lá'];
    let i = 0;
    setThinkMsg(msgs[0]);
    const id = setInterval(() => { i = (i + 1) % msgs.length; setThinkMsg(msgs[i]); }, 1600);
    return () => clearInterval(id);
  }, [phase]);

  // ---------- escutar comando ----------
  const listen = useCallback(async () => {
    if (!SR || activeListenRef.current) return;
    stopInterruptListener();
    stopAudio();  // deixa cortar a fala do NEXU ao começar a ouvir
    try {
      if (!navigator.permissions || (await navigator.permissions.query({ name: 'microphone' })).state !== 'granted') {
        const st = await navigator.mediaDevices.getUserMedia({ audio: true });
        st.getTracks().forEach(t => t.stop());
      }
    } catch { return; }
    activeListenRef.current = true;
    setPhase('listening'); setInterim('');
    const r = new SR();
    r.lang = 'pt-BR'; r.interimResults = true; r.continuous = false; r.maxAlternatives = 1;
    let finalText = '';
    r.onresult = (e) => {
      let itr = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t; else itr += t;
      }
      setInterim(itr || finalText);
    };
    r.onerror = () => {};
    r.onend = () => {
      activeListenRef.current = false;
      const txt = (finalText || '').trim(); setInterim('');
      if (txt) sendToNexu(txt);
      else { setPhase('idle'); setTimeout(() => { if (phaseRef.current === 'idle') closeOverlay(); }, 1500); }
    };
    recogRef.current = r;
    try { r.start(); } catch { activeListenRef.current = false; setPhase('idle'); }
  }, [sendToNexu]);

  // ---------- abrir / fechar ----------
  const openOverlay = useCallback(async () => {
    stopWake();
    // limpa qualquer sessão anterior pendurada (evita microfone/WS/AudioContext presos após inatividade)
    try { liveRef.current && liveRef.current.stop(); } catch {}
    liveRef.current = null;
    liveReadyRef.current = false;
    try { NexuLiveSession.unlockAudio(); } catch {}
    setActive(true); activeRef.current = true;
    setReply(''); setInterim(''); setTyped('');
    setPhase('thinking');
    // inicia a sessão Live (áudio bidirecional em tempo real)
    try {
      const sess = new NexuLiveSession({
        onStatus: (st) => { if (st === 'ouvindo' || st === 'pronto') { liveReadyRef.current = true; setPrepMsg(''); setPhase('listening'); } },
        onTranscriptIn: (t) => {
          // fala do usuário: acumula (a Live manda em pedaços) e MARCA novo passo do fluxo
          if (!inBufRef.current) newTurnRef.current = true; // início de uma nova fala = novo passo
          inBufRef.current += t;
          setInterim(inBufRef.current);
          // se o usuário pediu para fechar/finalizar, encerra o overlay
          if (userWantsToClose(inBufRef.current)) {
            setTimeout(() => { try { closeOverlay(); } catch {} }, 700);
          }
        },
        onTranscriptOut: (t) => {
          // resposta do NEXU: acumula NO TURNO atual (não junta com respostas antigas)
          replyBufRef.current += t;
          setReply(replyBufRef.current);
        },
        onText: (t) => { replyBufRef.current += t; setReply(replyBufRef.current); },
        onSpeakingChange: (sp) => {
          setPhase(sp ? 'speaking' : 'listening');
          if (sp) { setInterim(''); inBufRef.current = ''; }  // começou a falar → limpa a transcrição de entrada
        },
        onTurnStart: () => { replyBufRef.current = ''; },  // novo turno → zera a resposta
        onNavigate: (to) => {
          // navega SÓ depois que ele terminar de falar (não corta a voz)
          const go = () => { navigate(to); setTimeout(() => closeOverlay(), 400); };
          const waitSpeak = () => {
            if (phaseRef.current === 'speaking') { setTimeout(waitSpeak, 300); }
            else { setTimeout(go, 600); }
          };
          setTimeout(waitSpeak, 800);
        },
        onFilterPanel: (data) => { setFilterPanel(data); attachRich('filter', data); },
        onMediaPanel: (data) => { setMediaPanel(data); attachRich('media', data); },
        onGamesPanel: (data) => { setGamesPanel(data); attachRich('games', data); },
        onListPanel: (data) => setListPanel(data),
        onInputPanel: (data) => { setInputValue(''); setInputPanel(data); },
        onFinancePanel: (data) => setFinancePanel(data),
        onClosePanel: (data) => {
          const p = data?.panel;
          if (p === 'all') { setGamesPanel(null); setMediaPanel(null); setFilterPanel(null); setListPanel(null); setMessagePanel(null); setActionPanel(null); setInputPanel(null); }
          else if (p === 'games') setGamesPanel(null);
          else if (p === 'media') setMediaPanel(null);
          else if (p === 'filter') setFilterPanel(null);
          else if (p === 'list') setListPanel(null);
          else if (p === 'message') setMessagePanel(null);
          else if (p === 'action') setActionPanel(null);
          else if (p === 'finance') setFinancePanel(null);
          else if (p === 'input') setInputPanel(null);
        },
        onMessagePanel: (data) => setMessagePanel(data),
        // anexa dados ricos (filmes, clientes, jogos, links) ao último card do fluxo
        // (definida como closure abaixo via attachRich)
        onFlowStep: (data) => {
          const now = Date.now();
          const startsNewStep = newTurnRef.current;
          newTurnRef.current = false; // as próximas ações desta MESMA fala ficam no mesmo passo
          setFlowSteps(prev => {
            const last = prev[prev.length - 1];
            // novo passo se o usuário acabou de falar; senão, mesma fala = mesmo nível (irmão)
            const sameTurn = last && !startsNewStep;
            const level = sameTurn ? last.level : (last ? last.level + 1 : 0);
            let parents = [];
            if (sameTurn) parents = last.parents || [];
            else if (last) parents = prev.filter(x => x.level === last.level).map(x => x.id);
            const step = {
              id: 'fs_' + now + '_' + Math.random().toString(36).slice(2, 6),
              label: data.label, count: data.count, summary: data.summary,
              tool: data.tool, ids: data.ids || [], data: data.cardData || {}, level, parents, _ts: now,
            };
            return [...prev, step];
          });
        },
        onActionPanel: (m) => { setFilterPanel(null); setMediaPanel(null); setGamesPanel(null); setMessagePanel(null); setListPanel(null); setFinancePanel(null); setActionPanel({ title: m.title, action: m.action, items: m.items || [], done: false }); },
        onActionProgress: (m) => setActionPanel(p => {
          if (!p) return p;
          const items = [...p.items];
          if (items[m.index]) items[m.index] = { ...items[m.index], status: m.status, detalhe: m.detalhe };
          return { ...p, items };
        }),
        onActionDone: (m) => {
          setActionPanel(p => p ? { ...p, done: true, okCount: m.ok, total: m.total } : p);
          try { window.dispatchEvent(new CustomEvent('nexu-data-changed')); localStorage.setItem('nexu_data_changed', String(Date.now())); } catch {}
        },
        onVolume: (v) => {
          if (v.set !== null && v.set !== undefined) changeVolume(v.set);
          else if (v.delta) changeVolume(volume + v.delta);
        },
        onPreparing: (msg) => { setPrepMsg(msg); setPhase('thinking'); },
        onExecuting: (tool) => {
          const nomes = {
            buscar_cliente: 'Localizando cliente', relatorio_base: 'Analisando a base',
            listar_vencidos: 'Verificando vencidos', listar_vencendo: 'Verificando vencimentos',
            planos_do_cliente: 'Consultando planos', renovar_cliente: 'Renovando cliente',
            cobrar_em_massa: 'Preparando cobranças', excluir_vencidos: 'Removendo vencidos',
            excluir_cliente: 'Removendo cliente', importar_cliente: 'Importando cliente',
            importar_todos_da_integracao: 'Importando clientes', listar_integracoes: 'Buscando integrações',
            buscar_na_integracao: 'Buscando no painel', planos_da_integracao: 'Consultando planos',
            listar_revendas: 'Buscando revendas', adicionar_creditos_revenda: 'Adicionando créditos',
            bloquear_revenda: 'Bloqueando revenda', desbloquear_revenda: 'Desbloqueando revenda',
            transacoes_recentes: 'Buscando transações', relatorio_financeiro: 'Calculando receita',
          };
          setExecMsg(nomes[tool] || 'Executando ação');
          setPhase('thinking');
          setTimeout(() => setExecMsg(''), 4000);
        },
        onTurnComplete: () => {
          setExecMsg('');
          // finalização indireta: se o NEXU se despediu, encerra a sessão
          if (isFarewell(replyBufRef.current)) {
            setTimeout(() => closeOverlay(), 1800);
          }
        },
        onError: (msg) => {
          console.warn('[NEXU-LIVE]', msg);
          // qualquer erro/timeout de conexão antes de ficar PRONTO → cai no modo antigo (funciona)
          if (activeRef.current && !liveReadyRef.current) {
            liveReadyRef.current = false;
            try { liveRef.current && liveRef.current.stop(); } catch {}
            liveRef.current = null;
            speak('Sim, Senhor?').then(() => setTimeout(() => listen(), 150)).catch(() => {});
          }
        },
      });
      liveRef.current = sess;
      sess.setVolume(volume);
      await sess.start();
    } catch (e) {
      console.warn('[NEXU-LIVE] falha ao iniciar, usando modo antigo:', e);
      // fallback: modo antigo (transcrição navegador + TTS)
      await speak('Sim, Senhor?');
      setTimeout(() => listen(), 150);
    }
  }, [speak, listen]);

  const enviarInput = () => {
    const v = (inputValue || '').trim();
    if (!v) return;
    try { liveRef.current && liveRef.current.sendText(`O ${inputPanel?.campo || 'valor'} é: ${v}`); } catch {}
    setInputPanel(null); setInputValue('');
  };
  const closeOverlay = useCallback(() => {
    try { liveRef.current && liveRef.current.stop(); } catch {}
    liveRef.current = null;
    try { recogRef.current?.abort(); } catch {}
    stopAudio();
    activeListenRef.current = false;
    // dispara a animação de saída (núcleo colapsa), remove depois
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setActive(false); activeRef.current = false;
      setPhase('idle'); setInterim(''); setReply(''); setTyped(''); setFilterPanel(null); setActionPanel(null); setMediaPanel(null); setGamesPanel(null); setMessagePanel(null);
      if (wakeEnabledRef.current) setTimeout(() => { try { startWakeRef.current && startWakeRef.current(); } catch {} }, 600);
    }, 650);  // duração da animação de saída
  }, []);

  // ---------- wake word global ----------
  const stopWake = () => { try { wakeRef.current?.abort(); } catch {} wakeRef.current = null; };

  const startWake = useCallback(() => {
    if (!SR || !wakeEnabledRef.current || !canUse || activeRef.current) return;
    stopWake();
    const w = new SR();
    w.lang = 'pt-BR'; w.continuous = true; w.interimResults = true;
    w.onresult = (e) => {
      if (activeListenRef.current || activeRef.current) return;
      const t = Array.from(e.results).map(r => r[0].transcript).join(' ').toLowerCase();
      if (/(^|\s)(olá|ola|oi|ei|e aí|ê|hey)? ?(nexu|next|nexo|nexus|néxu|nex|néx)($|\s)/.test(t)) {
        try { w.stop(); } catch {}
        openOverlay();
      }
    };
    w.onend = () => {
      // reinicia enquanto o wake estiver ligado e o overlay fechado
      if (wakeEnabledRef.current && !activeRef.current && !activeListenRef.current) {
        setTimeout(() => { try { startWake(); } catch {} }, 500);
      }
    };
    w.onerror = () => {};
    wakeRef.current = w;
    try { w.start(); } catch {}
  }, [canUse, openOverlay]);

  useEffect(() => { startWakeRef.current = startWake; }, [startWake]);

  // auto-refresh dos jogos ao vivo a cada 2 min
  useEffect(() => {
    if (!gamesPanel || !gamesPanel.jogos) return;
    const temAoVivo = gamesPanel.jogos.some(j => j.situacao === 'ao_vivo' || j.situacao === 'agendado');
    if (!temAoVivo) return;
    const t = setInterval(async () => {
      try { const r = await api.get('/nexu/jogos-hoje'); if (r.data && r.data.jogos) setGamesPanel(r.data); } catch {}
    }, 120000);
    return () => clearInterval(t);
  }, [gamesPanel]);

  useEffect(() => {
    if (wakeEnabled && canUse && !isMobile) startWake(); else stopWake();
    return () => stopWake();
  }, [wakeEnabled, canUse, startWake]);

  useEffect(() => { if (window.speechSynthesis) window.speechSynthesis.getVoices(); }, []);

  if (!canUse) return null;

  const statusText = phase === 'listening' ? 'Ouvindo' : phase === 'thinking' ? (prepMsg || execMsg || thinkMsg || 'Processando') : phase === 'speaking' ? 'Respondendo' : '';

  return (
    <>
      {/* Botão discreto (canto inferior direito) */}
      {!active && (
        <button
          onClick={() => {
            try { NexuLiveSession.unlockAudio(); } catch {}
            if (isMobile) {
              // no celular, um toque ABRE o NEXU direto (wake contínuo não funciona no iOS Safari)
              openOverlay();
            } else {
              const v = !wakeEnabled; setWakeEnabled(v);
              try { localStorage.setItem('nexu_wake', v ? '1' : '0'); } catch {}
            }
          }}
          title={isMobile ? 'Abrir o NEXU' : (wakeEnabled ? 'Escuta "Olá NEXU" ativa' : 'Ativar comando de voz "Olá NEXU"')}
          className={isMobile
            ? 'fixed bottom-24 right-0 z-[9990] flex items-center justify-center w-11 h-11 rounded-l-full shadow-lg border border-r-0 border-cyan-400/30 bg-gradient-to-r from-cyan-500/90 to-primary/90 text-white pr-0.5 active:scale-95 transition-transform'
            : `fixed bottom-6 right-6 z-[9990] flex items-center gap-2 rounded-full pl-2.5 pr-3.5 py-2 shadow-lg border transition-all bg-gradient-to-r from-cyan-500/90 to-primary/90 text-white border-cyan-400/30`}
          style={isMobile ? { opacity: 0.92 } : undefined}
        >
          <span className="relative flex items-center justify-center w-6 h-6">
            <Mic className="w-4 h-4" />
            {(wakeEnabled && !isMobile) && <span className="absolute inset-0 rounded-full border border-white/40 animate-ping" />}
          </span>
          {!isMobile && <span className="text-xs font-medium">{wakeEnabled ? 'NEXU ativo' : 'Ativar voz'}</span>}
        </button>
      )}

      {/* Overlay JARVIS */}
      {active && (
        <div className={`fixed inset-0 z-[10000] flex flex-col items-center justify-center ${epicMode ? 'nexu-epic-bg' : 'nexu-jarvis-bg'} ${closing ? 'nexu-closing' : ''}`}
          onClick={(e) => { if (phaseRef.current === 'speaking' && liveRef.current) { liveRef.current.interrupt(); } else if (phaseRef.current === 'speaking') { stopAudio(); listen(); } }}>
          {/* Modo Épico: galáxia 3D fullscreen transparente como fundo, atrás de tudo */}
          {epicMode && <NexuThreeCore phase={phase} />}
          <button onClick={(e) => { e.stopPropagation(); closeOverlay(); }} className="absolute top-6 right-6 text-white/50 hover:text-white z-10"><X className="w-6 h-6" /></button>

          {/* Controle de volume */}
          <div className="absolute top-6 left-6 z-10 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => changeVolume(volume > 0 ? 0 : 1)} className="text-white/60 hover:text-white">
              {volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <input type="range" min="0" max="2" step="0.1" value={volume}
              onChange={(e) => changeVolume(parseFloat(e.target.value))}
              className="w-28 accent-cyan-400" />
            <span className="text-white/40 text-xs font-mono w-8">{Math.round(volume * 100)}%</span>
          </div>

          {/* Núcleo reator Jarvis */}
          {epicMode ? (
            <div className="pointer-events-none" style={{ width: 0, height: 0 }} />
          ) : (
          <div className="relative flex items-center justify-center" style={{ width: 280, height: 280 }}>
            {/* anéis externos girando */}
            <svg viewBox="0 0 280 280" className="absolute inset-0 w-full h-full">
              <circle cx="140" cy="140" r="130" fill="none" stroke="url(#g1)" strokeWidth="1" strokeDasharray="4 8" className="nexu-spin-slow" style={{ transformOrigin: 'center' }} opacity="0.5" />
              <circle cx="140" cy="140" r="108" fill="none" stroke="url(#g2)" strokeWidth="1.5" strokeDasharray="30 14" className="nexu-spin-rev" style={{ transformOrigin: 'center' }} opacity="0.6" />
              <circle cx="140" cy="140" r="88" fill="none" stroke="url(#g1)" strokeWidth="1" strokeDasharray="2 6" className="nexu-spin-med" style={{ transformOrigin: 'center' }} opacity="0.4" />
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#22d3ee" /><stop offset="100%" stopColor="#3b82f6" /></linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#06b6d4" /><stop offset="100%" stopColor="#8b5cf6" /></linearGradient>
              </defs>
            </svg>

            {/* pulsos de energia */}
            <span className="absolute rounded-full nexu-ring-pulse" style={{ width: 200, height: 200, borderColor: '#22d3ee' }} />
            <span className="absolute rounded-full nexu-ring-pulse" style={{ width: 200, height: 200, borderColor: '#3b82f6', animationDelay: '0.7s' }} />

            {/* barras de áudio (equalizer) quando fala/ouve */}
            {(phase === 'speaking' || phase === 'listening') && (
              <div className="absolute flex items-end gap-1 h-16">
                {Array.from({ length: 9 }).map((_, i) => (
                  <span key={i} className="w-1.5 rounded-full nexu-bar" style={{
                    background: phase === 'listening' ? '#22d3ee' : 'linear-gradient(to top,#3b82f6,#22d3ee)',
                    animationDelay: `${i * 0.08}s`,
                  }} />
                ))}
              </div>
            )}

            {/* núcleo central */}
            <div className={`relative rounded-full flex items-center justify-center ${phase === 'thinking' ? 'nexu-core-think' : 'nexu-core'}`}
              style={{ width: 120, height: 120, background: 'radial-gradient(circle at 35% 30%, #67e8f9, #06b6d4 45%, #1e3a8a 100%)', boxShadow: '0 0 60px rgba(34,211,238,.6), inset 0 0 30px rgba(255,255,255,.3)' }}>
              {phase === 'thinking'
                ? <div className="nexu-orbit"><span /><span /><span /></div>
                : <Mic className="w-9 h-9 text-white drop-shadow-lg" />}
            </div>
          </div>
          )}

          {/* status estiloso */}
          <div className="mt-10 text-center relative z-10">
            <p className="text-cyan-300 font-mono text-xs tracking-[0.4em] uppercase flex items-center justify-center gap-2">
              {statusText && <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />}
              {statusText}{statusText && <span className="nexu-dots" />}
            </p>
            {/* transcrição do que VOCÊ está falando (só enquanto fala) */}
            {interim && <p className="mt-4 text-white text-xl max-w-xl text-center px-6 font-light" style={{ textShadow: '0 2px 12px rgba(0,0,0,.9)' }}>"{interim}"</p>}
            {/* última resposta do NEXU — fica visível mesmo depois, para saber o que ele disse */}
            {reply && (
              <p className="mt-4 text-white text-base max-w-2xl text-center px-8 leading-relaxed" style={{ textShadow: '0 2px 12px rgba(0,0,0,.9)' }}>
                {reply}{phase === 'speaking' && <span className="nexu-cursor">|</span>}
              </p>
            )}
          </div>

          {/* Painel FINANCEIRO — gráfico animado + previsão */}
          {financePanel && (
            <div key="finance-panel" style={slotStyle('finance')} className={isMobile
              ? "fixed left-0 right-0 bottom-0 max-h-[85vh] rounded-t-3xl nexu-card border-t p-4 pt-3 flex flex-col nexu-sheet-in z-40"
              : "nexu-card rounded-2xl p-5 flex flex-col overflow-hidden z-40 nexu-panel-in"}
              onClick={(e) => e.stopPropagation()}>
              {isMobile && <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-3" />}
              <div className="flex items-center justify-between mb-4">
                <span className="text-base font-semibold text-cyan-300 flex items-center gap-2">💰 Faturamento</span>
                <button onClick={() => setFinancePanel(null)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
              </div>

              {/* Números principais */}
              <div className="grid grid-cols-2 gap-2.5 mb-4">
                <div className="rounded-xl bg-cyan-500/[0.08] border border-cyan-500/20 p-3">
                  <p className="text-[10px] text-white/50 uppercase tracking-wide">Recebido no mês</p>
                  <p className="text-xl font-bold text-cyan-300 nexu-count">R$ {Number(financePanel.recebido_mes || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
                </div>
                <div className="rounded-xl bg-purple-500/[0.08] border border-purple-500/20 p-3">
                  <p className="text-[10px] text-white/50 uppercase tracking-wide">MRR</p>
                  <p className="text-xl font-bold text-purple-300 nexu-count">R$ {Number(financePanel.mrr || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
                </div>
                <div className="rounded-xl bg-white/[0.04] border border-white/[0.08] p-3">
                  <p className="text-[10px] text-white/50 uppercase tracking-wide">Renovações</p>
                  <p className="text-lg font-bold text-white">{financePanel.renovacoes_mes || 0}</p>
                </div>
                <div className="rounded-xl bg-white/[0.04] border border-white/[0.08] p-3">
                  <p className="text-[10px] text-white/50 uppercase tracking-wide">Ticket médio</p>
                  <p className="text-lg font-bold text-white">R$ {Number(financePanel.ticket_medio || 0).toFixed(2)}</p>
                </div>
              </div>

              {/* Gráfico de barras animado — últimos 6 meses */}
              {financePanel.grafico && financePanel.grafico.valores && (
                <div className="mb-4">
                  <p className="text-[11px] text-white/50 mb-2">Últimos 6 meses</p>
                  <div className="flex items-end justify-between gap-2 h-32">
                    {financePanel.grafico.valores.map((v, i) => {
                      const max = Math.max(...financePanel.grafico.valores, 1);
                      const h = Math.max(4, (v / max) * 100);
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                          <span className="text-[9px] text-cyan-300 mb-1 opacity-0 nexu-bar-label" style={{ animationDelay: `${i*120 + 400}ms` }}>{v > 0 ? `${(v/1000).toFixed(1)}k` : '0'}</span>
                          <div className="w-full rounded-t-md bg-gradient-to-t from-cyan-600 to-cyan-300 nexu-bar-grow" style={{ height: `${h}%`, animationDelay: `${i*120}ms` }} />
                          <span className="text-[9px] text-white/40 mt-1">{financePanel.grafico.labels[i]}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Previsão de receita — próximos 30 dias */}
              <div className="rounded-xl bg-gradient-to-r from-green-500/[0.1] to-cyan-500/[0.08] border border-green-500/25 p-4 nexu-item-in">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">📈</span>
                  <p className="text-sm font-semibold text-green-300">Previsão — próximos 30 dias</p>
                </div>
                <p className="text-2xl font-bold text-green-300 nexu-count">R$ {Number(financePanel.previsao_30d || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</p>
                <p className="text-[11px] text-white/50 mt-1">{financePanel.clientes_a_vencer_30d || 0} cliente(s) vencem nesse período</p>
              </div>
            </div>
          )}

          {/* Painel de LISTA (cupons / templates) */}
          {inputPanel && (
            <div className="w-full max-w-md mx-auto animate-fade-in">
              <div className="rounded-2xl border border-white/[0.1] bg-white/[0.04] backdrop-blur-xl p-4 space-y-3">
                <p className="text-sm text-white font-medium">{inputPanel.titulo || 'Digite aqui'}</p>
                <input
                  autoFocus value={inputValue} onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') enviarInput(); if (e.key === 'Escape') setInputPanel(null); }}
                  placeholder={inputPanel.placeholder || 'Cole ou digite aqui...'}
                  className="w-full px-3 py-2.5 rounded-xl bg-black/30 border border-white/15 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-cyan-400/50" />
                <div className="flex gap-2">
                  <button onClick={() => setInputPanel(null)} className="flex-1 py-2 rounded-xl border border-white/15 text-white/60 text-sm hover:bg-white/5">Cancelar</button>
                  <button onClick={enviarInput} disabled={!inputValue.trim()} className="flex-1 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-primary text-white text-sm font-medium disabled:opacity-40">OK</button>
                </div>
              </div>
            </div>
          )}

          {listPanel && listPanel.itens && (
            <div key="list-panel" style={slotStyle('list')} className={isMobile
              ? "fixed left-0 right-0 bottom-0 max-h-[75vh] rounded-t-3xl nexu-card border-t p-4 pt-3 flex flex-col nexu-sheet-in z-40"
              : "nexu-card rounded-2xl p-4 flex flex-col overflow-hidden z-40 nexu-panel-in"}
              onClick={(e) => e.stopPropagation()}>
              {isMobile && <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-3" />}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-cyan-300 flex items-center gap-2">
                  {listPanel.tipo === 'cupons' ? '🎟️' : listPanel.tipo === 'sync' ? '🔄' : listPanel.tipo === 'mensagens' ? '💬' : '📝'} {listPanel.titulo}
                </span>
                <button onClick={() => setListPanel(null)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto space-y-2">
                {listPanel.itens.length === 0 && <p className="text-sm text-white/40 text-center py-4">Nenhum {listPanel.tipo === 'cupons' ? 'cupom' : 'template'} ainda.</p>}
                {listPanel.tipo === 'cupons' && listPanel.itens.map((c, i) => (
                  <div key={c.codigo} className={`rounded-xl border px-3 py-2.5 nexu-item-in transition-all ${c.destaque ? 'border-cyan-400 bg-cyan-500/10 shadow-[0_0_16px_2px_rgba(34,211,238,0.4)] nexu-highlight' : 'border-white/[0.08] bg-white/[0.03]'}`} style={{ animationDelay: `${i*50}ms` }}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-white">{c.codigo}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.ativo ? 'bg-green-500/15 text-green-400' : 'bg-white/10 text-white/40'}`}>{c.ativo ? 'ativo' : 'inativo'}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-white/50">
                      <span className="text-cyan-300">{c.desconto}</span>
                      <span>usos: {c.usos}</span>
                      <span>{c.alvo === 'customers' ? 'clientes' : c.alvo === 'plans' ? 'planos' : 'todos'}</span>
                    </div>
                  </div>
                ))}
                {listPanel.tipo === 'templates' && listPanel.itens.map((t, i) => (
                  <div key={i} className="rounded-xl border border-white/[0.08] bg-white/[0.03] px-3 py-2.5 nexu-item-in" style={{ animationDelay: `${i*50}ms` }}>
                    <p className="text-sm text-white font-medium">{t.nome}</p>
                    <p className="text-[11px] text-white/45 mt-0.5 line-clamp-2">{t.previa}</p>
                  </div>
                ))}
                {listPanel.tipo === 'mensagens' && listPanel.itens.map((m, i) => (
                  <div key={i} className="rounded-xl border border-white/[0.08] bg-white/[0.03] px-3 py-2.5 nexu-item-in" style={{ animationDelay: `${i*50}ms` }}>
                    <p className="text-sm text-white font-medium">{m.titulo}</p>
                    <p className="text-[11px] text-white/55 mt-0.5 whitespace-pre-wrap line-clamp-4">{m.texto}</p>
                  </div>
                ))}
                {listPanel.tipo === 'sync' && (listPanel.itens.length === 0
                  ? <p className="text-sm text-green-400 text-center py-4">✓ Todas as datas já estão iguais!</p>
                  : listPanel.itens.map((d, i) => (
                    <div key={i} className={`rounded-xl border px-3 py-2.5 nexu-item-in ${d.feito ? 'border-green-500/40 bg-green-500/[0.08]' : 'border-amber-500/25 bg-amber-500/[0.06]'}`} style={{ animationDelay: `${i*50}ms` }}>
                      <p className="text-sm text-white font-medium truncate flex items-center gap-1">{d.feito && <span className="text-green-400">✓</span>}{d.nome}</p>
                      <div className="flex items-center gap-2 mt-1 text-[12px]">
                        <span className="text-white/50 line-through">{d.atual}</span>
                        <span className="text-cyan-400">→</span>
                        <span className="text-green-400 font-medium">{d.painel}</span>
                      </div>
                    </div>
                  )))}
              </div>
            </div>
          )}

          {/* Painel de MENSAGEM de campanha (canto esquerdo, próprio, não sobrepõe os outros) */}
          {messagePanel && (
            <div key="msg" style={slotStyle('message')} className={isMobile
              ? "fixed left-0 right-0 top-0 max-h-[55vh] rounded-b-3xl nexu-card border-b p-4 flex flex-col nexu-sheet-in z-40"
              : "nexu-card rounded-2xl p-4 flex flex-col overflow-hidden z-40 nexu-panel-in"}
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-cyan-300 flex items-center gap-2 truncate">✏️ {messagePanel.titulo || 'Mensagem'}</span>
                <button onClick={() => setMessagePanel(null)} className="text-white/40 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
                <p className="text-sm text-white/90 whitespace-pre-wrap leading-relaxed">{messagePanel.mensagem}</p>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button onClick={() => { try { navigator.clipboard.writeText(messagePanel.mensagem); } catch {} }}
                  className="flex-1 py-2 rounded-xl bg-white/[0.06] text-white/70 text-xs hover:bg-white/[0.1]">Copiar</button>
                <span className="text-[11px] text-white/40 px-2">Peça ajustes por voz</span>
              </div>
            </div>
          )}

          {/* Painel de JOGOS do dia (placar ao vivo) */}
          {gamesPanel && gamesPanel.jogos && gamesPanel.jogos.length > 0 && (
            <div key="games" style={slotStyle('games')} className={isMobile
              ? "fixed left-0 right-0 bottom-0 max-h-[82vh] rounded-t-3xl nexu-card border-t p-4 pt-3 flex flex-col nexu-sheet-in z-40"
              : "nexu-card rounded-2xl p-4 flex flex-col overflow-hidden z-40 nexu-panel-in"}
              onClick={(e) => e.stopPropagation()}>
              {isMobile && <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-3" />}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-cyan-300 flex items-center gap-2">⚽ Jogos de hoje</span>
                <button onClick={() => setGamesPanel(null)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              {/* abas de filtro */}
              <div className="flex items-center gap-1 mb-3 overflow-x-auto pb-1">
                {[
                  { k: 'todos', label: 'Todos' },
                  { k: 'ao_vivo', label: '🔴 Ao vivo', n: gamesPanel.jogos.filter(j => j.situacao === 'ao_vivo').length },
                  { k: 'agendado', label: 'A começar', n: gamesPanel.jogos.filter(j => j.situacao === 'agendado').length },
                  { k: 'encerrado', label: 'Encerrados', n: gamesPanel.jogos.filter(j => j.situacao === 'encerrado').length },
                ].map(t => (
                  <button key={t.k} onClick={() => setGamesTab(t.k)}
                    className={`px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap transition-all ${gamesTab === t.k ? 'bg-cyan-500/80 text-white' : 'bg-white/[0.05] text-white/50 hover:text-white/80'}`}>
                    {t.label}{t.n != null ? ` ${t.n}` : ''}
                  </button>
                ))}
              </div>
              <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                {gamesPanel.jogos.filter(j => gamesTab === 'todos' || j.situacao === gamesTab).map((j, i) => (
                  <div key={i} className={`rounded-xl border px-3 py-2.5 nexu-item-in ${
                    j.situacao === 'ao_vivo' ? 'border-red-500/40 bg-red-500/[0.06]' :
                    j.situacao === 'encerrado' ? 'border-white/[0.06] bg-white/[0.02] opacity-70' :
                    'border-cyan-500/15 bg-white/[0.03]'}`} style={{ animationDelay: `${Math.min(i * 40, 500)}ms` }}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] text-white/40 truncate max-w-[70%]">{j.liga}</span>
                      {j.situacao === 'ao_vivo'
                        ? <span className="text-[10px] font-bold text-red-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />AO VIVO</span>
                        : j.situacao === 'encerrado'
                          ? <span className="text-[10px] text-white/40">encerrado</span>
                          : <span className="text-[10px] text-cyan-400">{j.hora}{j.dia && /aman/i.test(j.dia) ? <span className="text-amber-400 ml-1">amanhã</span> : ''}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        {j.casa_logo ? <img src={j.casa_logo} alt="" className="w-5 h-5 object-contain shrink-0" /> : null}
                        <span className="text-sm text-white truncate">{j.casa}</span>
                      </div>
                      {(j.situacao === 'ao_vivo' || j.situacao === 'encerrado')
                        ? <span className={`text-base font-bold px-2 shrink-0 ${j.situacao === 'ao_vivo' ? 'text-red-300' : 'text-white/80'}`}>{j.placar_casa || 0} - {j.placar_fora || 0}</span>
                        : <span className="text-xs text-white/30 px-2 shrink-0">x</span>}
                      <div className="flex items-center gap-1.5 flex-1 min-w-0 justify-end">
                        <span className="text-sm text-white truncate text-right">{j.fora}</span>
                        {j.fora_logo ? <img src={j.fora_logo} alt="" className="w-5 h-5 object-contain shrink-0" /> : null}
                      </div>
                    </div>
                    {j.canais && j.canais.length > 0 && j.situacao === 'agendado' && (
                      <p className="text-[10px] text-white/30 mt-1.5 truncate">📺 {j.canais.join(', ')}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Painel de MÍDIA (filmes/séries com capas, ou links) */}
          {mediaPanel && (
            <div key="media-panel" style={slotStyle('media')} className={isMobile
              ? "fixed left-0 right-0 bottom-0 max-h-[80vh] rounded-t-3xl nexu-card border-t p-4 pt-3 flex flex-col nexu-sheet-in z-40"
              : "nexu-card rounded-2xl p-4 flex flex-col overflow-hidden z-40 nexu-panel-in"}
              onClick={(e) => e.stopPropagation()}>
              {isMobile && <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-3" />}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-cyan-300 truncate">{mediaPanel.titulo || 'Resultados'}</span>
                <button onClick={() => setMediaPanel(null)} className="text-white/40 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
              </div>

              {/* LISTA de filmes/séries com capas */}
              {mediaPanel.tipo === 'lista' && (
                <div className="flex-1 overflow-y-auto grid grid-cols-3 gap-2.5 pr-1">
                  {(mediaPanel.itens || []).map((it, i) => (
                    <div key={i} className="rounded-xl overflow-hidden bg-white/[0.03] border border-white/[0.06] nexu-item-in" style={{ animationDelay: `${Math.min(i * 60, 700)}ms` }}>
                      {it.poster
                        ? <img src={it.poster} alt={it.nome} className="w-full aspect-[2/3] object-cover" loading="lazy" />
                        : <div className="w-full aspect-[2/3] bg-white/5 flex items-center justify-center text-3xl">🎬</div>}
                      <div className="p-1.5">
                        <p className="text-[11px] text-white font-medium leading-tight line-clamp-2">{it.nome}</p>
                        <p className="text-[10px] text-white/40 flex items-center gap-1">{it.ano}{it.nota ? <span className="text-amber-400">★ {it.nota}</span> : null}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* DETALHE de um filme/série */}
              {mediaPanel.tipo === 'detalhe' && mediaPanel.itens && mediaPanel.itens[0] && (
                <div className="flex-1 overflow-y-auto nexu-item-in">
                  <div className="flex gap-3">
                    {mediaPanel.itens[0].poster
                      ? <img src={mediaPanel.itens[0].poster} alt="" className="w-28 rounded-xl object-cover shrink-0 shadow-lg" />
                      : <div className="w-28 aspect-[2/3] rounded-xl bg-white/5 flex items-center justify-center text-4xl shrink-0">🎬</div>}
                    <div className="min-w-0">
                      <p className="text-base font-semibold text-white leading-tight">{mediaPanel.itens[0].nome}</p>
                      <p className="text-xs text-white/50 mt-1">{mediaPanel.itens[0].tipo_midia} · {mediaPanel.itens[0].ano}{mediaPanel.itens[0].generos ? ` · ${mediaPanel.itens[0].generos}` : ''}</p>
                      {mediaPanel.itens[0].serie_info && <p className="text-[11px] text-cyan-400/80 mt-1">{mediaPanel.itens[0].serie_info}</p>}
                      {mediaPanel.itens[0].nota ? <p className="text-sm text-amber-400 mt-1">★ {mediaPanel.itens[0].nota}/10</p> : null}
                    </div>
                  </div>
                  {mediaPanel.itens[0].sinopse && <p className="text-[13px] text-white/70 mt-3 leading-relaxed">{mediaPanel.itens[0].sinopse}</p>}
                  {mediaPanel.itens[0].elenco && mediaPanel.itens[0].elenco.length > 0 && (
                    <div className="mt-3">
                      <p className="text-[11px] text-white/40 mb-1">Elenco</p>
                      <p className="text-[13px] text-white/70">{mediaPanel.itens[0].elenco.join(', ')}</p>
                    </div>
                  )}
                </div>
              )}

              {/* LINKS (conhecimento geral) */}
              {mediaPanel.tipo === 'links' && (
                <div className="flex-1 overflow-y-auto space-y-2">
                  {(mediaPanel.links || []).map((l, i) => (
                    <a key={i} href={l.url} target="_blank" rel="noopener noreferrer"
                       className="block rounded-xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] px-4 py-3 transition-all nexu-item-in"
                       style={{ animationDelay: `${i * 60}ms` }} onClick={(e) => e.stopPropagation()}>
                      <p className="text-sm text-cyan-300 font-medium">{l.titulo}</p>
                      <p className="text-[11px] text-white/40">{l.fonte} ↗</p>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Painel de AÇÃO em tempo real (importação, exclusão, renovação) */}
          {actionPanel && (
            <div className={isMobile
              ? "fixed left-0 right-0 bottom-0 max-h-[75vh] rounded-t-3xl nexu-card border-t p-4 pt-3 flex flex-col nexu-sheet-in z-30"
              : "absolute left-6 top-20 bottom-20 w-80 max-w-[85vw] nexu-card rounded-2xl p-4 flex flex-col overflow-hidden nexu-panel-in z-30"}
              onClick={(e) => e.stopPropagation()}>
              {isMobile && <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-3" />}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-cyan-300">{actionPanel.title}</span>
                {actionPanel.done
                  ? <span className="text-xs text-green-400">✓ {actionPanel.okCount}/{actionPanel.total}</span>
                  : <span className="text-xs text-cyan-400/70">{actionPanel.items.filter(i => i.status === 'ok' || i.status === 'erro').length}/{actionPanel.items.length}</span>}
              </div>
              <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
                {actionPanel.items.map((it, i) => (
                  <div key={i} style={{ animationDelay: `${Math.min(i * 40, 600)}ms` }} className={`nexu-item-in rounded-lg border px-3 py-2 flex items-center gap-2.5 transition-all duration-300 ${
                    it.status === 'ok' ? 'border-green-500/30 bg-green-500/[0.06]' :
                    it.status === 'erro' ? 'border-red-500/30 bg-red-500/[0.06]' :
                    it.status === 'processando' ? 'border-cyan-500/40 bg-cyan-500/[0.08]' :
                    'border-white/[0.06] bg-white/[0.02]'}`}>
                    <span className="w-5 h-5 flex items-center justify-center shrink-0">
                      {it.status === 'ok' ? <span className="text-green-400 text-sm">✓</span> :
                       it.status === 'erro' ? <span className="text-red-400 text-sm">✕</span> :
                       it.status === 'processando' ? <span className="w-3.5 h-3.5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin inline-block" /> :
                       <span className="w-2 h-2 rounded-full bg-white/20" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm truncate ${it.status === 'processando' ? 'text-white' : 'text-white/80'}`}>{it.nome}</p>
                      <p className="text-[11px] text-white/35 truncate">{it.integracao}{it.detalhe ? ` · ${it.detalhe}` : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
              {actionPanel.done && (
                <button onClick={() => setActionPanel(null)} className="mt-3 w-full py-2 rounded-xl bg-white/[0.06] text-white/70 text-sm hover:bg-white/[0.1]">Fechar</button>
              )}
            </div>
          )}

          {/* Painel de filtro (lista de clientes filtrados) */}
          {filterPanel && filterPanel.total > 0 && (
            <div style={slotStyle('filter')} className={isMobile
              ? "fixed left-0 right-0 bottom-0 max-h-[75vh] rounded-t-3xl nexu-card border-t p-4 pt-3 flex flex-col nexu-sheet-in z-20"
              : "nexu-card rounded-2xl p-4 flex flex-col overflow-hidden z-20 nexu-panel-in"}
              onClick={(e) => e.stopPropagation()}>
              {isMobile && <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-3" />}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-cyan-300">{filterPanel.total} cliente(s)</span>
                <button onClick={() => setFilterPanel(null)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
                {filterPanel.clientes.map((c) => (
                  <div key={c.customer_id} className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-3 py-2">
                    <p className="text-sm text-white truncate">{c.nome}</p>
                    <p className="text-[11px] text-white/40 truncate">{[c.telefone, c.vence ? `vence ${c.vence}` : ''].filter(Boolean).join(' · ') || '—'}</p>
                  </div>
                ))}
                {filterPanel.total > filterPanel.clientes.length && (
                  <p className="text-[11px] text-white/30 text-center py-1">+{filterPanel.total - filterPanel.clientes.length} outros</p>
                )}
              </div>
              {filterPanel.is_reseller ? (
                <button onClick={() => { navigate('/resellers'); setTimeout(() => closeOverlay(), 300); }}
                  className="mt-3 w-full py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-primary text-white text-sm font-semibold hover:opacity-90">
                  Abrir Revendedores →
                </button>
              ) : filterPanel.is_integration ? (
                <button onClick={() => { navigate('/integrations'); setTimeout(() => closeOverlay(), 300); }}
                  className="mt-3 w-full py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-primary text-white text-sm font-semibold hover:opacity-90">
                  Abrir Integrações →
                </button>
              ) : (
                <button onClick={() => {
                    try { sessionStorage.setItem('nexu_filter_ids', JSON.stringify(filterPanel.filtro_ids || [])); } catch {}
                    navigate('/customers?nexu_filter=1');
                    setTimeout(() => closeOverlay(), 300);
                  }}
                  className="mt-3 w-full py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-primary text-white text-sm font-semibold hover:opacity-90">
                  Abrir no Gestor com esse filtro →
                </button>
              )}
            </div>
          )}

          {/* seletor de animação: Épico (3D) ou Simples — guardado em localStorage */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-white/[0.04] border border-white/[0.08] rounded-full p-1" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setEpicMode(false); try { localStorage.setItem('nexu_epic', '0'); } catch {} }}
              className={`px-3 py-1 rounded-full text-[11px] transition-all ${!epicMode ? 'bg-cyan-500/80 text-white' : 'text-white/50 hover:text-white/80'}`}>Simples</button>
            <button onClick={() => { setEpicMode(true); try { localStorage.setItem('nexu_epic', '1'); } catch {} }}
              className={`px-3 py-1 rounded-full text-[11px] transition-all flex items-center gap-1 ${epicMode ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white' : 'text-white/50 hover:text-white/80'}`}>✨ Épico 3D</button>
          </div>

          {/* dica de interromper */}
          {phase === 'speaking' && (
            <p className="absolute bottom-16 text-white/30 text-xs">Toque na tela para interromper e falar</p>
          )}
          {phase === 'idle' && (
            <button onClick={listen} className="absolute bottom-10 px-5 py-2.5 rounded-full bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 text-sm hover:bg-cyan-500/25">
              Falar de novo
            </button>
          )}
        </div>
      )}

      <style>{`
        .nexu-jarvis-bg { background: radial-gradient(ellipse at center, rgba(8,20,40,.85), rgba(0,0,0,.95)); backdrop-filter: blur(12px); animation: nexu-fade .35s ease both; }
        .nexu-epic-bg { background: radial-gradient(ellipse at 60% 40%, rgba(10,20,45,.55), rgba(2,4,12,.9) 70%); animation: nexu-fade .35s ease both; }
                .nexu-bar-grow { animation: nexu-bar-grow .8s cubic-bezier(.2,.9,.3,1) both; transform-origin: bottom; }
        @keyframes nexu-bar-grow { from { height: 0 !important; opacity: .3; } }
        .nexu-bar-label { animation: nexu-fade-in .4s ease forwards; }
        @keyframes nexu-fade-in { to { opacity: 1; } }
        .nexu-count { animation: nexu-count-in .6s ease both; }
        @keyframes nexu-count-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .nexu-highlight { animation: nexu-hl 1.2s ease; }
        @keyframes nexu-hl { 0% { transform: scale(1); } 30% { transform: scale(1.04); box-shadow: 0 0 24px 4px rgba(34,211,238,.6); } 100% { transform: scale(1); } }
        .nexu-flow-card { animation: nexu-card-in .5s cubic-bezier(.2,.8,.2,1) both; }
        @keyframes nexu-card-in { from { opacity: 0; transform: translateY(14px) scale(.92); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .nexu-flow-line { animation: nexu-line-draw .7s ease both, nexu-dash 1s linear infinite; stroke-dashoffset: 0; }
        @keyframes nexu-line-draw { from { opacity: 0; } to { opacity: 1; } }
        @keyframes nexu-dash { to { stroke-dashoffset: -20; } }
        .nexu-pulse { animation: nexu-pulse-a 2s ease-in-out infinite; }
        @keyframes nexu-pulse-a { 0%,100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34,211,238,.3); } 50% { transform: scale(1.05); box-shadow: 0 0 20px 4px rgba(34,211,238,.25); } }
        /* saída: o fundo dissolve e o conteúdo implode no centro */
        .nexu-closing { animation: nexu-bg-out .6s cubic-bezier(.5,0,.75,0) forwards; }
        @keyframes nexu-bg-out { from { opacity: 1; } to { opacity: 0; } }
        .nexu-closing > * { animation: nexu-implode .6s cubic-bezier(.6,0,.8,.2) forwards !important; }
        @keyframes nexu-implode {
          0% { opacity: 1; transform: scale(1); filter: blur(0); }
          40% { opacity: 1; transform: scale(1.08); }
          100% { opacity: 0; transform: scale(0.1) rotate(90deg); filter: blur(8px); }
        }
        @keyframes nexu-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes nexu-spin { to { transform: rotate(360deg); } }
        .nexu-spin-slow { animation: nexu-spin 24s linear infinite; }
        .nexu-spin-med { animation: nexu-spin 14s linear infinite; }
        .nexu-spin-rev { animation: nexu-spin 18s linear infinite reverse; }
        .nexu-ring-pulse { border-width: 1px; border-style: solid; opacity: 0; animation: nexu-ring 2.4s ease-out infinite; }
        @keyframes nexu-ring { 0% { transform: scale(.6); opacity: .7; } 100% { transform: scale(1.5); opacity: 0; } }
        .nexu-core { animation: nexu-breathe 3s ease-in-out infinite; }
        .nexu-core-think { animation: nexu-breathe 1.2s ease-in-out infinite; }
        @keyframes nexu-breathe { 0%,100% { transform: scale(1); } 50% { transform: scale(1.06); } }
        .nexu-bar { animation: nexu-eq .9s ease-in-out infinite; height: 20%; }
        @keyframes nexu-eq { 0%,100% { height: 15%; } 50% { height: 100%; } }
        .nexu-dots::after { content: ''; animation: nexu-dots 1.4s steps(4,end) infinite; }
        @keyframes nexu-dots { 0% { content: ''; } 25% { content: '.'; } 50% { content: '..'; } 75% { content: '...'; } }
        .nexu-orbit { position: relative; width: 44px; height: 44px; }
        .nexu-orbit span { position: absolute; top: 50%; left: 50%; width: 8px; height: 8px; margin: -4px; border-radius: 50%; background: #fff; box-shadow: 0 0 8px #fff; }
        .nexu-orbit span:nth-child(1) { animation: nexu-orbit 1s linear infinite; }
        .nexu-orbit span:nth-child(2) { animation: nexu-orbit 1s linear infinite; animation-delay: -.33s; }
        .nexu-orbit span:nth-child(3) { animation: nexu-orbit 1s linear infinite; animation-delay: -.66s; }
        @keyframes nexu-orbit { from { transform: rotate(0) translateX(18px); } to { transform: rotate(360deg) translateX(18px); } }
        .nexu-cursor { color: #22d3ee; animation: nexu-blink 0.9s step-end infinite; margin-left: 1px; }
        @keyframes nexu-blink { 50% { opacity: 0; } }
        @keyframes nexu-panel-in { from { opacity: 0; transform: translateX(30px); } to { opacity: 1; transform: translateX(0); } }
        /* ===== CARD 3D TEXTURIZADO — bem marcado, com profundidade e material ===== */
        .nexu-card {
          position: relative;
          background:
            radial-gradient(140% 100% at 15% -10%, rgba(34,211,238,.22), transparent 50%),
            radial-gradient(140% 120% at 95% 115%, rgba(139,92,246,.28), transparent 55%),
            linear-gradient(155deg, rgba(22,38,66,.96), rgba(9,16,34,.98) 60%, rgba(14,10,36,.98));
          border: 1.5px solid transparent;
          background-clip: padding-box;
          box-shadow:
            0 30px 70px -10px rgba(0,0,0,.85),
            0 8px 24px -6px rgba(34,211,238,.25),
            0 0 60px -10px rgba(139,92,246,.35),
            inset 0 2px 1px rgba(255,255,255,.12),
            inset 0 -20px 40px rgba(0,0,0,.4),
            inset 0 0 60px rgba(34,211,238,.06);
          backdrop-filter: blur(24px) saturate(1.5) brightness(1.05);
          -webkit-backdrop-filter: blur(24px) saturate(1.5) brightness(1.05);
          transform: translateZ(0);
          overflow: hidden;
        }
        /* moldura de brilho gradiente animado (borda "viva") */
        .nexu-card::before {
          content: ''; position: absolute; inset: -1.5px; border-radius: inherit; pointer-events: none; z-index: 0;
          padding: 1.5px;
          background: linear-gradient(135deg, rgba(34,211,238,.9), rgba(139,92,246,.5) 40%, rgba(34,211,238,.15) 70%, rgba(139,92,246,.8));
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
                  mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor; mask-composite: exclude;
          opacity: .7;
          background-size: 200% 200%;
          animation: nexu-border-flow 6s linear infinite;
        }
        @keyframes nexu-border-flow { 0% { background-position: 0% 0%; } 100% { background-position: 200% 200%; } }
        /* textura de material: grade fina + reflexo diagonal (dá sensação de vidro/metal) */
        .nexu-card::after {
          content: ''; position: absolute; inset: 0; border-radius: inherit; pointer-events: none; z-index: 0; opacity: .6;
          background-image:
            repeating-linear-gradient(0deg, rgba(255,255,255,.02) 0px, rgba(255,255,255,.02) 1px, transparent 1px, transparent 4px),
            repeating-linear-gradient(90deg, rgba(255,255,255,.015) 0px, rgba(255,255,255,.015) 1px, transparent 1px, transparent 4px),
            linear-gradient(120deg, transparent 30%, rgba(255,255,255,.06) 45%, transparent 60%);
          mix-blend-mode: overlay;
        }
        .nexu-card > * { position: relative; z-index: 1; }
        /* leve flutuação 3D dos cards no épico (respiração) */
        .nexu-card { animation: nexu-panel-in .4s ease both, nexu-float 6s ease-in-out infinite; }
        @keyframes nexu-float { 0%,100% { transform: translateY(0) translateZ(0); } 50% { transform: translateY(-5px) translateZ(0); } }
        .nexu-panel-in { animation: nexu-panel-in .35s cubic-bezier(.2,1,.3,1) both; }
        @keyframes nexu-sheet-in { from { opacity: 0; transform: translateY(100%); } to { opacity: 1; transform: translateY(0); } }
        .nexu-sheet-in { animation: nexu-sheet-in .4s cubic-bezier(.16,1,.3,1) both; }
        @keyframes nexu-item-in { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .nexu-item-in { animation: nexu-item-in .35s cubic-bezier(.2,1,.3,1) both; }
      `}</style>
    </>
  );
}
